<template>
  <div class="col-12" >
    <div class="card-body px-0 pb-2">
      <div class="p-4">
        <form @submit.prevent="handleSubmit" @keydown.enter.prevent>
          <div class="row g-3">
            <!-- Existing description field -->
            <div class="col-12">
              <label for="description" class="bmd-label-float">{{ $t('questionForm.description') }}</label>
              <textarea
                class="form-control borderless-input"
                :class="{ 'is-invalid': errors.description }"
                id="description"
                v-model.trim="formData.description"
                required
                rows="4"
                @blur="validateField('description')"
              ></textarea>
              <div class="invalid-feedback">{{ errors.description }}</div>
            </div>

           <!-- Class Selection Field -->
<div class="col-md-6">
  <label for="class_id" class="bmd-label-float">{{ $t('questionForm.class') }}</label>
  <select
    class="form-select borderless-input"
    :class="{ 'is-invalid': errors.class_id }"
    id="class_id"
    v-model="formData.class_id"
    required
    @change="handleClassChange"
    @blur="validateField('class_id')"
  >
    <option value="" disabled>{{ $t('questionForm.selectClass') }}</option>
    <option
      v-for="classItem in classes"
      :key="classItem.id"
      :value="classItem.id"
    >
      {{ classItem.name }}
    </option>
  </select>
  <div class="invalid-feedback">{{ errors.class_id }}</div>
</div>

<!-- Chapter -->
<div class="col-md-6">
  <label for="chapter_subject_id" class="bmd-label-float">{{ $t('questionForm.chapter') }}</label>
  <select
    class="form-select borderless-input"
    :class="{ 'is-invalid': errors.chapter_subject_id }"
    id="chapter_subject_id"
    v-model="formData.chapter_subject_id"
    required
    :disabled="!formData.subject_id || loadingChapters"
    @blur="validateField('chapter_subject_id')"
  >
    <option value="" disabled>{{ $t('questionForm.selectChapter') }}</option>
    <option v-if="loadingChapters" disabled>{{ $t('questionForm.loadingChapters') }}</option>
    <option v-else-if="chapterSubjects.length === 0" disabled>{{ $t('questionForm.noChapters') }}</option>
    <option
      v-else
      v-for="chapter in chapterSubjects"
      :key="chapter.id"
      :value="chapter.id"
    >
      {{ chapter.chapter?.name || chapter.name || $t('common.notAvailable') }}
    </option>
  </select>
  <div class="invalid-feedback">{{ errors.chapter_subject_id }}</div>
</div>

            <!-- Question Type -->
            <div class="col-md-6">
              <label for="question_type_id" class="bmd-label-float">{{ $t('questionForm.questionType') }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.question_type_id }"
                id="question_type_id"
                v-model="formData.question_type_id"
                required
                @change="handleQuestionTypeChange"
                @blur="validateField('question_type_id')"
              >
                <option value="">{{ $t('questionForm.selectQuestionType') }}</option>
                <option
                  v-for="type in questionTypes"
                  :key="type.id"
                  :value="type.id"
                >
                  {{ type.title }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.question_type_id }}</div>
            </div>

            <!-- Question Level -->
            <div class="col-md-6">
              <label for="question_level_id" class="bmd-label-float">{{ $t('questionForm.questionLevel') }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.question_level_id }"
                id="question_level_id"
                v-model="formData.question_level_id"
                required
                @blur="validateField('question_level_id')"
              >
                <option value="">{{ $t('questionForm.selectQuestionLevel') }}</option>
                <option
                  v-for="level in questionLevels"
                  :key="level.id"
                  :value="level.id"
                >
                  {{ level.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.question_level_id }}</div>
            </div>

            <!-- Teacher -->
            <div class="col-md-6" v-if="!isTeacher">
              <label for="teacher_id" class="bmd-label-float">{{ $t('questionForm.teacher') }}</label>
              <select
                class="form-select borderless-input"
                :class="{ 'is-invalid': errors.teacher_id }"
                id="teacher_id"
                v-model="formData.teacher_id"
                required
                @blur="validateField('teacher_id')"
              >
                <option value="">{{ $t('questionForm.selectTeacher') }}</option>
                <option
                  v-for="teacher in teachers"
                  :key="teacher.id"
                  :value="teacher.id"
                >
                  {{ teacher.username }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.teacher_id }}</div>
            </div>

            <!-- Question Image -->
            <div class="col-md-6">
              <label for="image" class="bmd-label-float">{{ $t('questionForm.questionImage') }}</label>
              <div
                class="card dashed-border p-3 text-center cursor-pointer"
                @click="$refs.fileInput.click()"
              >
                <input
                  type="file"
                  ref="fileInput"
                  id="image"
                  accept="image/*"
                  style="display: none"
                  @change="handleImageUpload"
                />

                <div v-if="loadingImage" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>

                <img
                  v-else-if="previewImage"
                  :src="previewImage"
                  class="img-fluid mx-auto d-block"
                  style="max-height: 150px"
                  alt="Question Image"
                />

                <div v-else class="py-4">
                  <i class="fas fa-image fs-1 text-muted"></i>
                  <p class="text-muted mt-2 mb-0">{{ $t('questionForm.clickToUpload') }}</p>
                </div>

                <button
                  v-if="previewImage"
                  @click.stop="clearImage"
                  class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
              <small class="text-muted d-block mt-1">{{ $t('questionForm.imageNote') }}</small>
            </div>

            <!-- Answers Section -->
            <div class="col-12" v-if="shouldShowAnswers">
              <hr />
              <h6>{{ $t('questionForm.answers') }}</h6>

              <!-- Multiple Choice Answers -->
              <div
                v-if="currentQuestionType?.name === 'multiple_choice'"
                class="answers-container"
              >
                <div
                  v-for="(answer, index) in formData.answers"
                  :key="index"
                  class="row g-2 mb-3"
                >
                  <div class="col-md-8">
                    <input
                      type="text"
                      class="form-control"
                      v-model="answer.answer"
                      :placeholder="$t('questionForm.multipleChoiceAnswer', { index: index + 1 })"
                      required
                    />
                  </div>
                  <div class="col-md-2">
                    <div class="form-check">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :id="`correct_${index}`"
                        v-model="answer.is_correct"
                      />
                      <label class="form-check-label" :for="`correct_${index}`">
                        {{ $t('questionForm.correct') }}
                      </label>
                    </div>
                  </div>
                  <div class="col-md-2">
                    <button
                      type="button"
                      class="btn btn-danger btn-sm"
                      @click="removeAnswer(index)"
                      :disabled="formData.answers.length <= 1"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-secondary btn-sm"
                  @click="addAnswer"
                >
                  <i class="fas fa-plus ms-2"></i>
                  {{ $t('questionForm.addAnswer') }}
                </button>
              </div>

              <!-- True/False -->
              <div
                v-if="currentQuestionType?.name === 'true_false'"
                class="answers-container"
              >
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    id="true_answer"
                    value="true"
                    v-model="trueFalseAnswer"
                  />
                  <label class="form-check-label" for="true_answer">{{ $t('questionForm.true') }}</label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    class="form-check-input"
                    type="radio"
                    id="false_answer"
                    value="false"
                    v-model="trueFalseAnswer"
                  />
                  <label class="form-check-label" for="false_answer">{{ $t('questionForm.false') }}</label>
                </div>
              </div>

              <!-- Short Answer -->
              <div
                v-if="currentQuestionType?.name === 'short_answer'"
                class="answers-container"
              >
                <div class="row g-2 mb-3">
                  <div class="col-md-12">
                    <input
                      type="text"
                      class="form-control"
                      v-model="shortAnswer"
                      :placeholder="$t('questionForm.shortAnswer')"
                      required
                    />
                  </div>
                </div>
              </div>

              <!-- Essay -->
              <div
                v-if="currentQuestionType?.name === 'essay'"
                class="answers-container"
              >
                <div class="row g-2 mb-3">
                  <div class="col-md-12">
                    <textarea
                      class="form-control"
                      v-model="essayAnswer"
                      :placeholder="$t('questionForm.essayAnswer')"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
              </div>

              <!-- Matching -->
              <div
                v-if="currentQuestionType?.name === 'matching'"
                class="answers-container"
              >
                <div
                  v-for="(answer, index) in formData.answers"
                  :key="index"
                  class="row g-2 mb-3"
                >
                  <div class="col-md-5">
                    <input
                      type="text"
                      class="form-control"
                      v-model="answer.question_part"
                      :placeholder="$t('questionForm.matchingPart', { index: index + 1 })"
                      required
                    />
                  </div>
                  <div class="col-md-5">
                    <input
                      type="text"
                      class="form-control"
                      v-model="answer.answer"
                      :placeholder="$t('questionForm.matchingAnswer', { index: index + 1 })"
                      required
                    />
                  </div>
                  <div class="col-md-2">
                    <button
                      type="button"
                      class="btn btn-danger btn-sm"
                      @click="removeAnswer(index)"
                      :disabled="formData.answers.length <= 1"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-secondary btn-sm"
                  @click="addAnswer"
                >
                  <i class="fas fa-plus ms-2"></i>
                  {{ $t('questionForm.addMatchingPair') }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
  defineEmits,
  defineProps,
  defineExpose,
} from "vue";
import { useStore } from 'vuex';
import { useToast } from "vue-toastification";
import api from "@/api";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useStore();
const props = defineProps({
  title: String,
  submitText: {
    type: String,
    default: "حفظ",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isEditMode: {
    type: Boolean,
    default: false,
  },
  subjectId: {
    type: [String, Number],
    default: null,
  },
  classId: {
    type: [String, Number],
    default: null,
  },
  initialData: {
    type: Object,
    default: () => ({
      description: "",
      image: "",
      class_id: "",
      subject_id: "",
      chapter_subject_id: "",
      question_type_id: "",
      question_level_id: "",
      teacher_id: "",
      answers: [],
    }),
  },
});
const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === 'teacher');


const emit = defineEmits([
  "submit",
  "cancel",
  "validation-error",
  "update:form-data",
]);
const toast = useToast();


const formData = ref({
  description: "",
  image: "",
  class_id: "",
  subject_id: "",
  chapter_subject_id: "",
  question_type_id: "",
  question_level_id: "",
  teacher_id: "",
  answers: [],
});

const classes = ref([]);
const classSubjects = ref([]);
const loadingSubjects = ref(false);
const errors = ref({});
const chapterSubjects = ref([]);
const questionTypes = ref([]);
const questionLevels = ref([]);
const teachers = ref([]);
const loadingChapters = ref(false);
const fileInput = ref(null);
const loadingImage = ref(false);
const previewImage = ref(props.initialData.image_url || null);

const needsAnswers = (qtName) =>
  qtName && qtName !== 'information';

const validateAnswers = () => {
  const qt = currentQuestionType.value?.name;
  const ans = formData.value.answers || [];

  // Generic empties check for any answers-based type
  const anyEmpty = ans.some(a =>
    (qt === 'matching'
      ? (!a.question_part || !a.answer)
      : (!a.answer && qt !== 'essay')) // essay can be blank if you want; change if needed
  );

  // MULTIPLE CHOICE
  if (qt === 'multiple_choice') {
    if (ans.length < 2) {
      return { valid: false, message: t('questionForm.atLeastTwoAnswers') };
    }
    if (anyEmpty) {
      return { valid: false, message: t('questionForm.fillAllChoices') };
    }
    const correctCount = ans.filter(a => !!a.is_correct).length;
    if (correctCount === 0) {
      return { valid: false, message: t('questionForm.pickCorrectAnswer') }; // اختر الإجابة الصحيحة
    }
    // If you want *exactly one* correct, uncomment:
    // if (correctCount > 1) {
    //   return { valid: false, message: t('questionForm.onlyOneCorrectAllowed') };
    // }
    return { valid: true };
  }

  // TRUE/FALSE
  if (qt === 'true_false') {
    if (!ans[0]?.answer || !['true','false'].includes(ans[0].answer)) {
      return { valid: false, message: t('questionForm.pickTrueOrFalse') };
    }
    return { valid: true };
  }

  if (qt === 'short_answer') {
    if (!ans[0]?.answer) {
      return { valid: false, message: t('questionForm.enterModelAnswer') };
    }
    return { valid: true };
  }

  if (qt === 'essay') {

    return { valid: true };
  }

  if (qt === 'matching') {
    if (ans.length < 2) {
      return { valid: false, message: t('questionForm.atLeastOneMatchingPair') };
    }
    if (anyEmpty) {
      return { valid: false, message: t('questionForm.fillAllPairs') };
    }
    return { valid: true };
  }

  // Non-answer types (or info)
  return { valid: true };
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (!file) {
    clearImage();
    return;
  }

  const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
  if (!validTypes.includes(file.type.toLowerCase()) || file.size > 2 * 1024 * 1024) {
    toast.error(t("questionForm.invalidImage"));
    clearImage();
    return;
  }

  formData.value.image = file;
  const reader = new FileReader();
  reader.onload = (e) => {
    previewImage.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const clearImage = () => {
  formData.value.image = null;
  formData.value.image_url = null;
  previewImage.value = null;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const initializeFormData = () => {
  formData.value = {
    description: props.initialData.description || "",
    class_id: props.initialData.class_id || "",
    subject_id: props.initialData.subject_id || "",
    chapter_subject_id: props.initialData.chapter_subject_id || "",
    question_type_id: props.initialData.question_type_id || "",
    question_level_id: props.initialData.question_level_id || "",
    teacher_id: isTeacher.value ? currentUser.value.id : props.initialData.teacher_id || "",
    image: null,
    image_url: props.initialData.image 
      ? props.initialData.image.startsWith('http') 
        ? props.initialData.image 
        : `/storage/${props.initialData.image}`
      : null,
    answers: props.initialData.answers
      ? props.initialData.answers.map((a) => ({
          ...a,
          is_correct: Boolean(a.is_correct),
          answer: a.answer || "",
          question_part: a.question_part || "",
          ...(a.answer_id ? { answer_id: a.answer_id } : {}),
        }))
      : [],
  };
  
  if (formData.value.image_url) {
    previewImage.value = formData.value.image_url;
  }
};


const currentQuestionType = computed(() => {
  return questionTypes.value.find(
    (type) => type.id == formData.value.question_type_id
  );
});

const shouldShowAnswers = computed(() => {
  return (
    formData.value.question_type_id &&
    currentQuestionType.value?.name !== "information"
  );
});



const handleClassChange = async () => {
  formData.value.subject_id = "";
  formData.value.chapter_subject_id = "";
  classSubjects.value = [];

  if (formData.value.class_id) {
    await fetchClassSubjects();

    if (classSubjects.value && classSubjects.value.length === 1) {
      formData.value.subject_id = classSubjects.value[0].id;
      await handleSubjectChange();
    }
  }
};

const validateField = (field) => {
  if (!formData.value[field]) {
errors.value[field] = t('questionForm.requiredField');
    return false;
  }
  delete errors.value[field];
  return true;
};

const validateForm = () => {
  errors.value = {};

  const validations = [
    validateField("description"),
    validateField("class_id"),
    validateField("subject_id"),
    validateField("chapter_subject_id"),
    validateField("question_type_id"),
    validateField("question_level_id"),
    validateField("teacher_id"),
  ];

  // Centralized answers validation
  if (shouldShowAnswers.value && needsAnswers(currentQuestionType.value?.name)) {
    const ansCheck = validateAnswers();
    if (!ansCheck.valid) {
      errors.value.answers = ansCheck.message;
      validations.push(false);
    }
  }

  return validations.every(Boolean);
};


const fetchClassSubjects = async () => {
  if (!formData.value.class_id) return;

  loadingSubjects.value = true;
  try {
    const response = await api.get(
      `/classes/${formData.value.class_id}/subjects`
    );
    classSubjects.value = response.data.data.map((item) => item.subject);
  } catch (error) {
    toast.error(t("questionForm.errorFetchingClassSubjects"));
    console.error("Error fetching class subjects:", error);
    classSubjects.value = [];
  } finally {
    loadingSubjects.value = false;
  }
};

const handleSubjectChange = async () => {
  formData.value.chapter_subject_id = "";
  if (formData.value.subject_id) {
    await fetchChapterSubjects();

    if (chapterSubjects.value.length === 1) {
      formData.value.chapter_subject_id = chapterSubjects.value[0].id;
    }
  }
};

const handleQuestionTypeChange = () => {
  if (!currentQuestionType.value) return;

  if (!formData.value.answers) {
    formData.value.answers = [];
  }

  switch (currentQuestionType.value.name) {
    case "multiple_choice":
      if (
        formData.value.answers.length === 0 ||
        !Object.prototype.hasOwnProperty.call(
          formData.value.answers[0] || {},
          "is_correct"
        )
      ) {
        formData.value.answers = [
          { answer: "", is_correct: false, answer_id: null },
          { answer: "", is_correct: false, answer_id: null },
        ];
      }
      break;

    case "true_false":
      if (
        formData.value.answers.length === 0 ||
        !["true", "false"].includes(formData.value.answers[0]?.answer)
      ) {
        formData.value.answers = [
          { answer: "true", is_correct: true, answer_id: null },
        ];
      }
      break;

    case "short_answer":
    case "essay":
      if (formData.value.answers.length === 0) {
        formData.value.answers = [
          { answer: "", is_correct: true, answer_id: null },
        ];
      }
      break;

    case "matching":
      if (
        formData.value.answers.length === 0 ||
        !Object.prototype.hasOwnProperty.call(
          formData.value.answers[0] || {},
          "question_part"
        )
      ) {
        formData.value.answers = [
          { question_part: "", answer: "", is_correct: true, answer_id: null },
          { question_part: "", answer: "", is_correct: true, answer_id: null },
        ];
      }
      break;

    default:
      formData.value.answers = [];
  }
};

const fetchChapterSubjects = async () => {
  if (!formData.value.subject_id) return;

  loadingChapters.value = true;
  try {
    const response = await api.get("/chapter-subjects", {
      params: {
        subject_id: formData.value.subject_id,
        class_id: formData.value.class_id,
      },
    });
    chapterSubjects.value = response.data.data;
  } catch (error) {
    toast.error(t("questionForm.errorFetchingChapters"));
    console.error("Error fetching chapters:", error);
    chapterSubjects.value = [];
  } finally {
    loadingChapters.value = false;
  }
};

const fetchClasses = async () => {
  try {
    if (isTeacher.value) {
      const response = await api.get(`/teachers/${currentUser.value.id}/classes`);
      classes.value = response.data.data;
      
      if (classes.value.length === 1) {
        formData.value.class_id = classes.value[0].id;
        await fetchClassSubjects();
      }
    } else {
  const response = await api.get('/classes', {
    params: {
      all: true
    }
  });      classes.value = response.data.data;
    }
  } catch (error) {
    toast.error(t("questionForm.errorFetchingClasses"));
    console.error("Error fetching classes:", error);
  }
};
const trueFalseAnswer = computed({
  get() {
    if (!formData.value.answers || formData.value.answers.length === 0) {
      return "";
    }
    return formData.value.answers[0]?.answer || "";
  },
  set(value) {
    if (!formData.value.answers || formData.value.answers.length === 0) {
      formData.value.answers = [{ answer: value, is_correct: true }];
    } else {
      formData.value.answers[0].answer = value;
    }
  },
});

const shortAnswer = computed({
  get() {
    if (!formData.value.answers || formData.value.answers.length === 0) {
      return "";
    }
    return formData.value.answers[0]?.answer || "";
  },
  set(value) {
    if (!formData.value.answers || formData.value.answers.length === 0) {
      formData.value.answers = [{ answer: value, is_correct: true }];
    } else {
      formData.value.answers[0].answer = value;
    }
  },
});

const essayAnswer = computed({
  get() {
    if (!formData.value.answers || formData.value.answers.length === 0) {
      return "";
    }
    return formData.value.answers[0]?.answer || "";
  },
  set(value) {
    if (!formData.value.answers || formData.value.answers.length === 0) {
      formData.value.answers = [{ answer: value, is_correct: true }];
    } else {
      formData.value.answers[0].answer = value;
    }
  },
});

const fetchQuestionTypes = async () => {
  try {
    const response = await api.get("/question-types");
    questionTypes.value = response.data.data;
  } catch (error) {
    toast.error(t("questionForm.errorFetchingQuestionTypes"));
    console.error("Error fetching question types:", error);
  }
};

const fetchQuestionLevels = async () => {
  try {
    const response = await api.get("/question-levels");
    questionLevels.value = response.data.data;
  } catch (error) {
    toast.error(t("questionForm.errorFetchingQuestionLevels"));
    console.error("Error fetching question levels:", error);
  }
};

const fetchTeachers = async () => {
  try {
    const response = await api.get("/teachers");
    teachers.value = response.data.data;
  } catch (error) {
    toast.error(t("questionForm.errorFetchingTeachers"));
    console.error("Error fetching teachers:", error);
  }
};

const addAnswer = () => {
  if (!currentQuestionType.value) return;

  switch (currentQuestionType.value.name) {
    case "multiple_choice":
      formData.value.answers.push({
        answer: "",
        is_correct: false,
        answer_id: null,
      });
      break;

    case "matching":
      formData.value.answers.push({
        question_part: "",
        answer: "",
        is_correct: true,
        answer_id: null,
      });
      break;
  }
};

const removeAnswer = (index) => {
  if (!currentQuestionType.value) return;

  if (
    (currentQuestionType.value.name === "multiple_choice" ||
      currentQuestionType.value.name === "matching") &&
    formData.value.answers.length > 1
  ) {
    formData.value.answers.splice(index, 1);
  }
};
const handleSubmit = () => {
  if (!validateForm()) {
    if (errors.value.answers) {
      toast.error(errors.value.answers);
      const el = document.querySelector('.answers-container');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      toast.warning(t("questionForm.fixErrors"));
    }
    emit("validation-error");
    return;
  }

  const submissionData = new FormData();

  Object.keys(formData.value).forEach(key => {
    if (key !== 'answers' && key !== 'image') {
      submissionData.append(key, formData.value[key]);
    }
  });

  if (formData.value.answers) {
    const answersArray = formData.value.answers.map(answer => ({
      answer: answer.answer,
      is_correct: answer.is_correct,
      question_part: answer.question_part || null,
      answer_id: answer.answer_id || null
    }));
        submissionData.append('answers', JSON.stringify(answersArray));
  }

  if (formData.value.image instanceof File) {
    submissionData.append('image', formData.value.image);
  } else if (formData.value.image_url && !formData.value.image) {
    submissionData.append('image_url', formData.value.image_url);
  }

  emit("submit", submissionData);
};

const submitForm = () => {
  handleSubmit();
};
watch(() => formData.value.question_type_id, () => {
  handleQuestionTypeChange();
  const qt = currentQuestionType.value?.name;
  if (needsAnswers(qt)) {
    toast.info(t('questionForm.remindPickCorrect')); 
  }
});

onMounted(async () => {
  initializeFormData();

  await Promise.all([
    fetchClasses(),
    fetchQuestionTypes(),
    fetchQuestionLevels(),
    fetchTeachers(),
  ]);

  if (formData.value.class_id) {
    await fetchClassSubjects();
  }
  if (formData.value.subject_id) {
    await fetchChapterSubjects();
  }

  if (formData.value.question_type_id) {
    handleQuestionTypeChange();
  }
});

watch(
  () => props.classId,
  async (newVal) => {
    if (newVal) {
      formData.value.class_id = newVal;
      await handleClassChange();
    }
  }
);

watch(
  () => props.subjectId,
  async (newVal) => {
    if (newVal && !props.isEditMode) {
      formData.value.subject_id = newVal;
      await handleSubjectChange();
    }
  }
);

watch(
  () => props.initialData,
  (newData) => {
    if (newData && Object.keys(newData).length > 0) {
      initializeFormData();
    }
  },
  { deep: true }
);
watch(
  () => formData.value.class_id,
  async (newClassId) => {
    if (newClassId) {
      await fetchClassSubjects();
      if (classSubjects.value.length === 1) {
        formData.value.subject_id = classSubjects.value[0].id;
        await handleSubjectChange();
      }
    }
  },
  { immediate: true }
);

watch(
  () => formData.value.subject_id,
  async (newSubjectId) => {
    if (newSubjectId) {
      await fetchChapterSubjects();
      if (chapterSubjects.value.length === 1) {
        formData.value.chapter_subject_id = chapterSubjects.value[0].id;
      }
    }
  },
  { immediate: true }
);
watch(
  () => formData.value,
  (newVal) => {
    emit("update:form-data", {
      ...newVal,
      answers: newVal.answers.map((a) => ({
        ...a,
        is_correct: Boolean(a.is_correct),
      })),
    });
  },
  { deep: true, immediate: true }
);

defineExpose({
  submitForm,
  formData: formData.value,
});
</script>

<style scoped>
.answers-container {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
}
</style>